import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import Modal from '../components/Modal';
import { ARTICLES } from '../src/data/articles';

interface DossierEntry {
  id: string;
  fileNo: string;
  category: string;
  year: string;
  designer: string;
  title: string;
  description: string;
  extendedDescription?: string;
  technicalSpecs?: string;
  location: string;
  status: 'preserved' | 'deteriorating' | 'destroyed';
  imageUrl: string;
  galleryImages?: string[];
}

const Dossier: React.FC = () => {
  const { t } = useTranslation();
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [selectedEntry, setSelectedEntry] = useState<DossierEntry | null>(null);

  const categories = [
    { id: 'all', label: t('dossier.categories.all') },
    { id: 'product', label: t('dossier.categories.product') },
    { id: 'graphic', label: t('dossier.categories.graphic') },
    { id: 'industrial', label: t('dossier.categories.industrial') },
    { id: 'furniture', label: t('dossier.categories.furniture') },
  ];

  // Sample dossier entries
  const entries: DossierEntry[] = [
    {
      id: '1',
      fileNo: 'YU-DSR-1955-012',
      category: 'industrial',
      year: '1955',
      designer: 'Rade Končar',
      title: t('dossier.entries.1.title'),
      description: t('dossier.entries.1.description'),
      location: t('dossier.entries.1.location'),
      status: 'preserved',
      imageUrl: '/dossier/entry-1.jpg',
    },
    {
      id: '2',
      fileNo: 'YU-DSR-1971-088',
      category: 'graphic',
      year: '1971',
      designer: 'Mirko Ilić',
      title: t('dossier.entries.2.title'),
      description: t('dossier.entries.2.description'),
      location: t('dossier.entries.2.location'),
      status: 'preserved',
      imageUrl: '/dossier/entry-2.jpg',
    },
    {
      id: '3',
      fileNo: 'YU-DSR-1977-034',
      category: 'product',
      year: '1977',
      designer: 'Zastava Design Team',
      title: t('dossier.entries.3.title'),
      description: t('dossier.entries.3.description'),
      location: t('dossier.entries.3.location'),
      status: 'destroyed',
      imageUrl: '/dossier/entry-3.jpg',
    },
    {
      id: '4',
      fileNo: 'YU-DSR-1968-056',
      category: 'furniture',
      year: '1968',
      designer: 'Niko Kralj',
      title: t('dossier.entries.4.title'),
      description: t('dossier.entries.4.description'),
      location: t('dossier.entries.4.location'),
      status: 'preserved',
      imageUrl: '/dossier/entry-4.jpg',
    },
    {
      id: '5',
      fileNo: 'YU-DSR-1984-099',
      category: 'graphic',
      year: '1984',
      designer: 'Ivica Osim Visual Team',
      title: t('dossier.entries.5.title'),
      description: t('dossier.entries.5.description'),
      location: t('dossier.entries.5.location'),
      status: 'preserved',
      imageUrl: '/dossier/entry-5.jpg',
    },
    {
      id: '6',
      fileNo: 'YU-DSR-1960-022',
      category: 'industrial',
      year: '1960',
      designer: 'Gorenje Industrial Design',
      title: t('dossier.entries.6.title'),
      description: t('dossier.entries.6.description'),
      location: t('dossier.entries.6.location'),
      status: 'deteriorating',
      imageUrl: '/dossier/entry-6.jpg',
    },
  ];

  const filteredEntries =
    selectedCategory === 'all'
      ? entries
      : entries.filter(entry => entry.category === selectedCategory);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'preserved':
        return 'bg-green-600';
      case 'deteriorating':
        return 'bg-yellow-600';
      case 'destroyed':
        return 'bg-[#C41E3A]';
      default:
        return 'bg-gray-600';
    }
  };

  return (
    <>
      <Helmet>
        <title>Yugoslav Design Dossier - History, Architecture & Culture | Born in YU</title>
        <meta
          name="description"
          content="Explore Yugoslav design history, brutalist architecture, and socialist culture. Articles on Spomenik monuments, Yugoslav designers, and cultural heritage of former Yugoslavia."
        />
        <meta
          name="keywords"
          content="Yugoslav design, brutalist architecture, Spomenik monuments, Yugoslav history, socialist architecture, Yugoslav culture, Balkan architecture"
        />
        <link rel="canonical" href="https://borninyu.com/dossier" />
      </Helmet>

      <div className="min-h-screen bg-white">
        {/* Header */}
        <div className="border-b-4 border-black bg-white p-8 md:p-16">
          <div className="max-w-7xl mx-auto">
            {/* Classification Banner */}
            <div className="flex items-center justify-between mb-6">
              <div className="border-2 border-black bg-[#C41E3A] text-white px-4 py-2 text-[10px] font-black uppercase">
                {t('dossier.classification')}
              </div>
              <div className="text-[10px] font-bold uppercase tracking-widest">
                ENTRIES: {entries.length} | ARTICLES: {ARTICLES.length}
              </div>
            </div>

            {/* Title */}
            <h1 className="text-5xl md:text-7xl font-black uppercase leading-none mb-4 tracking-tighter">
              The Dossier
            </h1>

            {/* Subtitle */}
            <p className="text-lg font-bold uppercase text-gray-700 max-w-3xl">
              Historical Documents & Design Archive
            </p>
          </div>
        </div>

        {/* Blog Articles Section */}
        {ARTICLES.length > 0 && (
          <div className="border-b-4 border-black bg-gray-50 p-8 md:p-16">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter">
                  {t('dossier.historicalDocuments')}
                </h2>
                <div className="border-2 border-black bg-black text-white px-3 py-1 text-xs font-black uppercase">
                  {t('dossier.articlesLabel')}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {ARTICLES.map((article) => (
                  <Link
                    key={article.id}
                    to={`/dossier/${article.slug}`}
                    className="border-4 border-black bg-white hover:shadow-[8px_8px_0px_0px_rgba(0,0,0,1)] transition-all group"
                  >
                    {/* Article Image */}
                    <div className="aspect-[16/9] bg-gray-200 border-b-4 border-black relative overflow-hidden">
                      <img
                        src={article.featuredImage}
                        alt={article.featuredImageAlt}
                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all"
                        onError={(e) => {
                          e.currentTarget.style.display = 'none';
                        }}
                      />
                      {/* Category Badge */}
                      <div className="absolute top-4 right-4 border-2 border-black bg-[#C41E3A] text-white px-3 py-1 text-xs font-black uppercase z-20">
                        {article.category}
                      </div>
                    </div>

                    {/* Article Content */}
                    <div className="p-6">
                      <div className="text-xs font-typewriter text-gray-500 uppercase mb-2">
                        {new Date(article.publishDate).toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'short',
                          day: 'numeric',
                        })}
                        {' • '}
                        {article.readTime}
                      </div>

                      <h3 className="text-xl font-black uppercase leading-tight mb-3 tracking-tighter group-hover:text-[#C41E3A] transition-colors">
                        {article.title}
                      </h3>

                      <p className="text-sm text-gray-700 leading-relaxed mb-4 line-clamp-3">
                        {article.subtitle}
                      </p>

                      <div className="flex flex-wrap gap-2">
                        {article.tags.slice(0, 3).map((tag) => (
                          <span
                            key={tag}
                            className="px-2 py-1 border border-black text-[10px] font-black uppercase bg-gray-100"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Design Archive Header */}
        <div className="border-b-4 border-black bg-white p-8 md:p-12">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-black uppercase tracking-tighter mb-2">
              Design Archive
            </h2>
            <p className="text-sm font-bold uppercase text-gray-600">
              Catalogued Yugoslav Design Objects
            </p>
          </div>
        </div>

        {/* Category Filter */}
        <div className="border-b-3 border-black bg-gray-100 p-4 md:p-6 sticky top-0 z-10">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-wrap gap-3">
              {categories.map(category => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 text-xs font-black uppercase border-2 border-black transition-all ${
                    selectedCategory === category.id
                      ? 'bg-black text-white'
                      : 'bg-white text-black hover:bg-gray-200'
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Dossier Entries Grid */}
        <div className="max-w-7xl mx-auto p-8 md:p-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {filteredEntries.map(entry => (
              <div
                key={entry.id}
                className="border-4 border-black bg-white hover:shadow-[12px_12px_0px_0px_rgba(0,0,0,1)] transition-all cursor-pointer"
                onClick={() => setSelectedEntry(entry)}
              >
                {/* Header with File Number */}
                <div className="border-b-3 border-black bg-black text-white p-4">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-black uppercase tracking-wider">
                      {t('dossier.fileNo')}: {entry.fileNo}
                    </div>
                    <div
                      className={`${getStatusColor(entry.status)} text-white text-[8px] font-black uppercase px-2 py-1`}
                    >
                      {t(`dossier.status.${entry.status}`)}
                    </div>
                  </div>
                </div>

                {/* Image Placeholder */}
                <div className="aspect-[16/9] bg-gray-200 border-b-3 border-black relative overflow-hidden">
                  <img
                    src={entry.imageUrl}
                    alt={entry.title}
                    className="absolute inset-0 w-full h-full object-cover z-10"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 z-0">
                    <div className="text-6xl font-black text-gray-500 opacity-20">{entry.year}</div>
                  </div>
                  {/* Confidential Stamp Overlay */}
                  <div className="absolute top-4 right-4 border-3 border-[#C41E3A] bg-white px-3 py-2 rotate-12 z-20">
                    <div className="text-[#C41E3A] text-xs font-black uppercase text-center leading-tight">
                      {t('dossier.confidential')}
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-6">
                  {/* Designer */}
                  <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider mb-2">
                    {t('dossier.designer')}: {entry.designer}
                  </div>

                  {/* Title */}
                  <h3 className="text-2xl font-black uppercase leading-tight mb-3 tracking-tighter">
                    {entry.title}
                  </h3>

                  {/* Description */}
                  <p className="text-sm leading-relaxed mb-4 text-gray-700">{entry.description}</p>

                  {/* Meta Info */}
                  <div className="grid grid-cols-2 gap-3 text-[10px] font-bold uppercase">
                    <div className="border border-black px-2 py-1">
                      {t('dossier.year')}: {entry.year}
                    </div>
                    <div className="border border-black px-2 py-1">
                      {t(`dossier.categories.${entry.category}`)}
                    </div>
                    <div className="col-span-2 border border-black px-2 py-1">
                      {t('dossier.location')}: {entry.location}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* No Results */}
          {filteredEntries.length === 0 && (
            <div className="text-center py-16">
              <div className="text-4xl font-black uppercase text-gray-400 mb-4">
                {t('dossier.noResults')}
              </div>
              <p className="text-sm uppercase font-bold text-gray-500">
                {t('dossier.noResultsDescription')}
              </p>
            </div>
          )}
        </div>

        {/* Footer Note */}
        <div className="border-t-4 border-black bg-gray-100 p-8">
          <div className="max-w-7xl mx-auto">
            <div className="text-xs font-bold uppercase text-gray-600 text-center">
              {t('dossier.footer.note')}
            </div>
          </div>
        </div>

        {/* Modal */}
        <Modal isOpen={!!selectedEntry} onClose={() => setSelectedEntry(null)}>
          {selectedEntry && (
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left: Image */}
              <div className="border-b-4 md:border-b-0 md:border-r-4 border-black">
                <div className="aspect-square bg-gray-200 relative overflow-hidden flex items-center justify-center">
                  <img
                    src={selectedEntry.imageUrl}
                    alt={selectedEntry.title}
                    className="w-full h-full object-contain z-10"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                  <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-br from-gray-300 to-gray-400 z-0">
                    <div className="text-9xl font-black text-gray-500 opacity-10">
                      {selectedEntry.year}
                    </div>
                  </div>
                  {/* Confidential Stamp */}
                  <div className="absolute top-8 right-8 border-4 border-[#C41E3A] text-[#C41E3A] px-6 py-3 rotate-12 bg-white/90 z-20">
                    <div className="text-xl font-black uppercase tracking-wider">
                      {t('dossier.modal.confidential')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right: Info */}
              <div className="p-8 md:p-12 overflow-y-auto max-h-[80vh]">
                {/* File Number Header */}
                <div className="border-4 border-black bg-black text-white p-4 mb-6">
                  <div className="flex items-center justify-between">
                    <div className="text-xs font-black uppercase tracking-wider">
                      {t('dossier.modal.fileNumber')}: {selectedEntry.fileNo}
                    </div>
                    <div
                      className={`px-3 py-1 text-xs font-black uppercase ${
                        selectedEntry.status === 'preserved'
                          ? 'bg-green-600'
                          : selectedEntry.status === 'deteriorating'
                            ? 'bg-yellow-600'
                            : 'bg-[#C41E3A]'
                      }`}
                    >
                      {t(`dossier.status.${selectedEntry.status}`)}
                    </div>
                  </div>
                </div>

                {/* Category Badge */}
                <div className="border-2 border-black px-3 py-1 text-[10px] font-black uppercase inline-block mb-6">
                  {t(`dossier.categories.${selectedEntry.category}`)}
                </div>

                {/* Title */}
                <h2 className="text-4xl md:text-5xl font-black uppercase leading-none mb-4 tracking-tighter">
                  {selectedEntry.title}
                </h2>

                {/* Meta Info */}
                <div className="space-y-3 mb-6 text-sm font-bold uppercase">
                  <div className="flex gap-2">
                    <span className="text-gray-500">{t('dossier.modal.designer')}:</span>
                    <span>{selectedEntry.designer}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-500">{t('dossier.modal.year')}:</span>
                    <span>{selectedEntry.year}</span>
                  </div>
                  <div className="flex gap-2">
                    <span className="text-gray-500">{t('dossier.modal.location')}:</span>
                    <span>{selectedEntry.location}</span>
                  </div>
                </div>

                {/* Description */}
                <div className="border-t-2 border-black pt-6">
                  <h3 className="text-xs font-black uppercase mb-3 text-gray-500">
                    {t('dossier.modal.description')}
                  </h3>
                  <p className="text-sm leading-relaxed mb-4">{selectedEntry.description}</p>
                  {selectedEntry.extendedDescription && (
                    <p className="text-sm leading-relaxed text-gray-700">
                      {selectedEntry.extendedDescription}
                    </p>
                  )}
                </div>

                {/* Technical Specs */}
                {selectedEntry.technicalSpecs && (
                  <div className="border-t-2 border-black mt-6 pt-6">
                    <h3 className="text-xs font-black uppercase mb-3 text-gray-500">
                      {t('dossier.modal.technicalSpecs')}
                    </h3>
                    <p className="text-xs leading-relaxed text-gray-700 font-mono">
                      {selectedEntry.technicalSpecs}
                    </p>
                  </div>
                )}

                {/* Historical Context */}
                <div className="border-t-2 border-black mt-6 pt-6">
                  <h3 className="text-xs font-black uppercase mb-3 text-gray-500">
                    {t('dossier.modal.historicalContext')}
                  </h3>
                  <p className="text-xs leading-relaxed text-gray-700">
                    {t('dossier.modal.historicalContextDescription')}
                  </p>
                </div>
              </div>
            </div>
          )}
        </Modal>
      </div>
    </>
  );
};

export default Dossier;
